import React from "react";
import { useTheme } from "styled-components";
import useEmblaCarousel from "embla-carousel-react";

import { SERVICE_TYPE } from "@typings/globals";

import { CSSGrid } from "@components/Layout/CssGrid";

import { TestWrapper } from "@ui/Atoms";

import { useTranslate } from "@hooks/useTranslate";
import { useResize } from "@hooks/useResize";
import { useConfig } from "@hooks/useConfig";

import {
  tabsOrder,
  servicesTranslations,
  emblaOptions,
  servicesTestIds,
} from "./Tabs.helpers";
import * as Styled from "./Tabs.styled";
import { b2cAreas } from "./Tabs.b2c";
import { b2bAreas } from "./Tabs.b2b";
import { endOfTenancyAreas } from "./Tabs.endOfTenancy";

export const Tabs: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const { windowWidth } = useResize();

  const availableServices = useConfig<SERVICE_TYPE[]>(["availableServices"]);
  const services = tabsOrder.filter(service =>
    availableServices.includes(service),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const [height, setHeight] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<SERVICE_TYPE>(
    SERVICE_TYPE.HOME_CLEANING,
  );

  const $tabsWrapper = React.useRef<HTMLDivElement>(null);

  const clickHandler = (SERVICE: SERVICE_TYPE, index: number) => {
    setActiveTab(SERVICE);

    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  };

  React.useEffect(() => {
    if (services.length === 1) return;
    if (!$tabsWrapper.current) return;

    const $activeTab = $tabsWrapper.current.querySelector(
      "[data-active='true']",
    ) as HTMLDivElement;

    setHeight($activeTab?.clientHeight || null);
  }, [$tabsWrapper.current, activeTab, windowWidth, services.length]);

  return (
    <Styled.Root>
      {services.length > 1 && (
        <Styled.Embla ref={emblaRef}>
          <Styled.EmblaContent>
            {services.map((SERVICE, index) => (
              <Styled.EmblaSlide key={SERVICE}>
                <TestWrapper testId={servicesTestIds[SERVICE]}>
                  <Styled.NavButton
                    type="button"
                    $isActive={SERVICE === activeTab}
                    onClick={clickHandler.bind(null, SERVICE, index)}
                  >
                    {translate(servicesTranslations[SERVICE])}
                  </Styled.NavButton>
                </TestWrapper>
              </Styled.EmblaSlide>
            ))}
          </Styled.EmblaContent>
        </Styled.Embla>
      )}

      <Styled.TabsWrapper ref={$tabsWrapper} $height={height}>
        {services.includes(SERVICE_TYPE.HOME_CLEANING) && (
          <Styled.Tab
            data-active={SERVICE_TYPE.HOME_CLEANING === activeTab}
            $isActive={SERVICE_TYPE.HOME_CLEANING === activeTab}
          >
            {/* theme.spacing.spacing_24 for mobile */}
            <CSSGrid areas={b2cAreas} rowGap={theme.spacing.spacing_32} />
          </Styled.Tab>
        )}

        {services.includes(SERVICE_TYPE.B2B) && (
          <Styled.Tab
            data-active={SERVICE_TYPE.B2B === activeTab}
            $isActive={SERVICE_TYPE.B2B === activeTab}
          >
            <CSSGrid areas={b2bAreas} rowGap={theme.spacing.spacing_32} />
          </Styled.Tab>
        )}

        {services.includes(SERVICE_TYPE.END_OF_TENANCY) && (
          <Styled.Tab
            data-active={SERVICE_TYPE.END_OF_TENANCY === activeTab}
            $isActive={SERVICE_TYPE.END_OF_TENANCY === activeTab}
          >
            <CSSGrid
              areas={endOfTenancyAreas}
              rowGap={theme.spacing.spacing_32}
            />
          </Styled.Tab>
        )}
      </Styled.TabsWrapper>
    </Styled.Root>
  );
};
